import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import {
  ALERT_LEVEL_LABEL,
  ALERT_RULE_LABEL,
  ALERT_RULES_FOR,
  INTAKE_TYPE_LABEL,
  PERSON_FIELD_TYPE_LABEL,
  type AlertLevel,
  type CheckinQuestion,
  type CheckoutSettings,
  type FieldAlertRule,
  type IntakeQuestionType,
  type PersonFieldListItem,
  type PersonFieldType,
  type ReminderPolicy,
} from '../lib/checkouts';
import { useToast } from './toast';
import { Alert, Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, Field, Input, Modal, PageLoader, Select, Switch, Table, Td, Textarea, Th } from './ui';

type SettingsPatch = Partial<Omit<CheckoutSettings, 'mailbox'>>;

const lines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

function useSaveSettings(message: string) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) => api.put<CheckoutSettings>('/checkout-settings', patch),
    onSuccess: (saved) => {
      queryClient.setQueryData(['checkout-settings'], saved);
      void queryClient.invalidateQueries({ queryKey: ['borrower-alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['loaner-options'] });
      toast.success(message);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

/** Settings → Checkouts & loaners: borrower alerts, person fields, the check-in form, loaners, and return reminders. */
export function CheckoutSettingsPanel() {
  const settings = useQuery({ queryKey: ['checkout-settings'], queryFn: () => api.get<CheckoutSettings>('/checkout-settings') });
  if (settings.isLoading) return <PageLoader />;
  if (settings.error || !settings.data) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  return (
    <div className="space-y-6">
      <BuiltInAlerts settings={settings.data} />
      <PersonFieldSettings />
      <CheckinFormSettings />
      <LoanerSettings settings={settings.data} />
      <ReminderSettings settings={settings.data} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Borrower alerts
// ---------------------------------------------------------------------------

function BuiltInAlerts({ settings }: { settings: CheckoutSettings }) {
  const save = useSaveSettings('Borrower alerts saved');
  const [balance, setBalance] = useState(settings.openBalanceAlert);
  const [overdue, setOverdue] = useState(settings.overdueDevicesAlert);
  const rows = [
    { label: 'Unpaid damage invoices', description: 'A damage invoice was sent to their family and isn’t paid yet.', value: balance, onChange: setBalance },
    { label: 'Devices already overdue', description: 'Something checked out to them is past its due date.', value: overdue, onChange: setOverdue },
  ];

  return (
    <Card>
      <CardHeader
        title="Borrower alerts"
        description="Shown before a device goes out to someone. A warning has to be read first; a block stops the check-out unless an administrator overrides it."
      />
      <div className="max-w-2xl space-y-4 p-5">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">{row.label}</p>
              <p className="text-xs text-slate-500">{row.description}</p>
            </div>
            <Select value={row.value} onChange={(event) => row.onChange(event.target.value as AlertLevel)} className="w-48">
              {(Object.keys(ALERT_LEVEL_LABEL) as AlertLevel[]).map((level) => (
                <option key={level} value={level}>
                  {ALERT_LEVEL_LABEL[level]}
                </option>
              ))}
            </Select>
          </div>
        ))}
        <p className="text-xs text-slate-500">
          Someone no longer active in the directory, or borrowing a second loaner, always gets a warning. Add alerts of your own with person fields.
        </p>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate({ openBalanceAlert: balance, overdueDevicesAlert: overdue })}>
          Save
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Person fields
// ---------------------------------------------------------------------------

interface FieldDraft {
  name: string;
  description: string;
  type: PersonFieldType;
  options: string;
  alertRule: FieldAlertRule;
  alertValue: string;
  alertLevel: AlertLevel;
  alertMessage: string;
}

const EMPTY_FIELD: FieldDraft = { name: '', description: '', type: 'CHECKBOX', options: '', alertRule: 'NONE', alertValue: '', alertLevel: 'WARN', alertMessage: '' };

const FIELD_EXAMPLES: FieldDraft[] = [
  { ...EMPTY_FIELD, name: 'Do not lend', type: 'CHECKBOX', alertRule: 'CHECKED', alertLevel: 'BLOCK', alertMessage: 'Do not lend devices' },
  { ...EMPTY_FIELD, name: 'Insurance expires', type: 'DATE', alertRule: 'DATE_PASSED', alertLevel: 'WARN', alertMessage: 'Device insurance has lapsed' },
];

const ruleText = (field: Pick<PersonFieldListItem, 'alertRule' | 'alertValue'>) =>
  field.alertRule === 'EQUALS' ? `When it’s ${field.alertValue}` : ALERT_RULE_LABEL[field.alertRule];

function PersonFieldSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fields = useQuery({
    queryKey: ['person-fields', 'settings'],
    queryFn: () => api.get<{ items: PersonFieldListItem[] }>('/person-fields', { archived: true }),
    select: (data) => data.items,
  });
  const [editing, setEditing] = useState<{ field: PersonFieldListItem | null; draft: FieldDraft } | null>(null);
  const [deleting, setDeleting] = useState<PersonFieldListItem | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['person-fields'] });
    void queryClient.invalidateQueries({ queryKey: ['borrower-alerts'] });
  };
  const archive = useMutation({
    mutationFn: (field: PersonFieldListItem) => api.patch(`/person-fields/${field.id}`, { archived: !field.archived }),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (field: PersonFieldListItem) => api.delete(`/person-fields/${field.id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Field deleted');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const toDraft = (field: PersonFieldListItem): FieldDraft => ({
    name: field.name,
    description: field.description ?? '',
    type: field.type,
    options: field.options.join('\n'),
    alertRule: field.alertRule,
    alertValue: field.alertValue ?? '',
    alertLevel: field.alertLevel === 'OFF' ? 'WARN' : field.alertLevel,
    alertMessage: field.alertMessage ?? '',
  });

  return (
    <Card>
      <CardHeader
        title="Person fields"
        description="Details kept on students and staff, like a do-not-lend flag or when device insurance expires. Each can raise an alert when devices are lent."
        actions={
          <Button size="sm" icon={<Plus />} onClick={() => setEditing({ field: null, draft: EMPTY_FIELD })}>
            Add field
          </Button>
        }
      />
      {fields.isLoading ? (
        <PageLoader />
      ) : fields.error ? (
        <ErrorState error={fields.error} onRetry={() => fields.refetch()} />
      ) : fields.data!.length === 0 ? (
        <div className="p-5">
          <p className="text-sm text-slate-600">No fields yet. Start from one of these:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {FIELD_EXAMPLES.map((draft) => (
              <Button key={draft.name} size="sm" icon={<Plus />} onClick={() => setEditing({ field: null, draft })}>
                {draft.name}
              </Button>
            ))}
          </div>
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Field</Th>
              <Th>Kind</Th>
              <Th>Alert</Th>
              <Th>People</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {fields.data!.map((field) => (
              <tr key={field.id} className={field.archived ? 'text-slate-400' : undefined}>
                <Td>
                  <p className="font-medium text-slate-900">
                    {field.name} {field.archived && <Badge>Archived</Badge>}
                  </p>
                  {field.description && <p className="text-xs text-slate-500">{field.description}</p>}
                </Td>
                <Td>
                  {PERSON_FIELD_TYPE_LABEL[field.type]}
                  {field.type === 'SELECT' && <p className="text-xs text-slate-500">{field.options.join(', ')}</p>}
                </Td>
                <Td>
                  {field.alertRule === 'NONE' || field.alertLevel === 'OFF' ? (
                    <span className="text-slate-400">None</span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge tone={field.alertLevel === 'BLOCK' ? 'red' : 'amber'}>{field.alertLevel === 'BLOCK' ? 'Blocks' : 'Warns'}</Badge>
                      <span className="text-xs text-slate-600">{ruleText(field)}</span>
                    </div>
                  )}
                </Td>
                <Td>{field.personCount}</Td>
                <Td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing({ field, draft: toDraft(field) })}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => archive.mutate(field)}>
                    {field.archived ? 'Restore' : 'Archive'}
                  </Button>
                  {field.personCount === 0 && (
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(field)} aria-label={`Delete ${field.name}`} />
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing && <FieldModal field={editing.field} initial={editing.draft} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        title="Delete this field?"
        description={`${deleting?.name ?? 'The field'} will be removed. Nobody has a value for it.`}
        confirmLabel="Delete"
        loading={remove.isPending}
      />
    </Card>
  );
}

function FieldModal({ field, initial, onClose, onSaved }: { field: PersonFieldListItem | null; initial: FieldDraft; onClose(): void; onSaved(): void }) {
  const toast = useToast();
  const [draft, setDraft] = useState(initial);
  const set = <K extends keyof FieldDraft>(key: K, value: FieldDraft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const options = lines(draft.options);
  const typeLocked = !!field && field.personCount > 0;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name,
        description: draft.description.trim() || null,
        type: draft.type,
        options: draft.type === 'SELECT' ? options : [],
        alertRule: draft.alertRule,
        alertValue: draft.alertRule === 'EQUALS' ? draft.alertValue.trim() || null : null,
        alertLevel: draft.alertLevel,
        alertMessage: draft.alertRule === 'NONE' ? null : draft.alertMessage.trim() || null,
      };
      return field ? api.patch(`/person-fields/${field.id}`, body) : api.post('/person-fields', body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(field ? 'Field saved' : 'Field added');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={field ? `Edit ${field.name}` : 'Add a person field'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="person-field" variant="primary" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="person-field" onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={draft.name} onChange={(event) => set('name', event.target.value)} maxLength={80} required autoFocus />
          </Field>
          <Field label="Kind" hint={typeLocked ? 'People have values, so it can’t change' : undefined}>
            <Select
              value={draft.type}
              disabled={typeLocked}
              onChange={(event) => {
                const type = event.target.value as PersonFieldType;
                setDraft((current) => ({ ...current, type, alertRule: ALERT_RULES_FOR[type].includes(current.alertRule) ? current.alertRule : 'NONE' }));
              }}
            >
              {(Object.keys(PERSON_FIELD_TYPE_LABEL) as PersonFieldType[]).map((type) => (
                <option key={type} value={type}>
                  {PERSON_FIELD_TYPE_LABEL[type]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Description">
          <Input value={draft.description} onChange={(event) => set('description', event.target.value)} maxLength={300} placeholder="What it means, for whoever fills it in" />
        </Field>
        {draft.type === 'SELECT' && (
          <Field label="Choices" hint="One per line">
            <Textarea rows={4} value={draft.options} onChange={(event) => set('options', event.target.value)} />
          </Field>
        )}
        <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <Field label="Alert when devices are lent">
            <Select value={draft.alertRule} onChange={(event) => set('alertRule', event.target.value as FieldAlertRule)}>
              {ALERT_RULES_FOR[draft.type].map((rule) => (
                <option key={rule} value={rule}>
                  {ALERT_RULE_LABEL[rule]}
                </option>
              ))}
            </Select>
          </Field>
          {draft.alertRule === 'EQUALS' && (
            <Field label="Value" required>
              {draft.type === 'SELECT' ? (
                <Select value={draft.alertValue} onChange={(event) => set('alertValue', event.target.value)} required>
                  <option value="">Choose one</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input value={draft.alertValue} onChange={(event) => set('alertValue', event.target.value)} maxLength={80} required />
              )}
            </Field>
          )}
          {draft.alertRule !== 'NONE' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Then">
                <Select value={draft.alertLevel} onChange={(event) => set('alertLevel', event.target.value as AlertLevel)}>
                  <option value="WARN">Warn</option>
                  <option value="BLOCK">Block the check-out</option>
                </Select>
              </Field>
              <Field label="The alert says" hint="Blank uses the field’s name">
                <Input value={draft.alertMessage} onChange={(event) => set('alertMessage', event.target.value)} maxLength={200} placeholder="e.g. Do not lend devices" />
              </Field>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// The check-in form
// ---------------------------------------------------------------------------

function CheckinFormSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const questions = useQuery({
    queryKey: ['checkin-questions', 'all'],
    queryFn: () => api.get<{ items: CheckinQuestion[] }>('/checkin-questions', { archived: true }),
    select: (data) => data.items,
  });
  const [editing, setEditing] = useState<{ question: CheckinQuestion | null } | null>(null);
  const [deleting, setDeleting] = useState<CheckinQuestion | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['checkin-questions'] });
  const archive = useMutation({
    mutationFn: (question: CheckinQuestion) => api.patch(`/checkin-questions/${question.id}`, { archived: !question.archived }),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const move = useMutation({
    mutationFn: async ({ index, by }: { index: number; by: -1 | 1 }) => {
      const order = [...(questions.data ?? [])];
      const [moved] = order.splice(index, 1);
      order.splice(index + by, 0, moved);
      await Promise.all(order.map((question, position) => (question.sortOrder === position ? null : api.patch(`/checkin-questions/${question.id}`, { sortOrder: position }))));
    },
    onSuccess: refresh,
    onError: (err) => {
      refresh();
      toast.error(errorMessage(err));
    },
  });
  const remove = useMutation({
    mutationFn: (question: CheckinQuestion) => api.delete(`/checkin-questions/${question.id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Question removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const list = questions.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Check-in form"
        description="Questions asked when a device comes back, like whether its charger did too. Answers are kept on the checkout. Batch check-ins and the Blackbaud add-in skip the form."
        actions={
          <Button size="sm" icon={<Plus />} onClick={() => setEditing({ question: null })}>
            Add question
          </Button>
        }
      />
      {questions.isLoading ? (
        <PageLoader />
      ) : questions.error ? (
        <ErrorState error={questions.error} onRetry={() => questions.refetch()} />
      ) : list.length === 0 ? (
        <p className="p-5 text-sm text-slate-500">No questions, so a check-in asks only for the device’s condition. Photos can always be added.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((question, index) => (
            <li key={question.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
              <div className="flex flex-col">
                <button
                  type="button"
                  disabled={index === 0 || move.isPending}
                  onClick={() => move.mutate({ index, by: -1 })}
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label={`Move ${question.label} up`}
                >
                  <ArrowUp className="size-3.5" />
                </button>
                <button
                  type="button"
                  disabled={index === list.length - 1 || move.isPending}
                  onClick={() => move.mutate({ index, by: 1 })}
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label={`Move ${question.label} down`}
                >
                  <ArrowDown className="size-3.5" />
                </button>
              </div>
              <div className={question.archived ? 'min-w-0 flex-1 opacity-60' : 'min-w-0 flex-1'}>
                <p className="font-medium text-slate-900">{question.label}</p>
                <p className="text-xs text-slate-500">
                  {INTAKE_TYPE_LABEL[question.type]}
                  {question.type === 'SELECT' && `: ${question.options.join(', ')}`}
                </p>
              </div>
              {question.required && <Badge tone="brand">Required</Badge>}
              {question.archived && <Badge>Archived</Badge>}
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing({ question })}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => archive.mutate(question)}>
                  {question.archived ? 'Restore' : 'Archive'}
                </Button>
                <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(question)} aria-label={`Remove ${question.label}`} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && <QuestionModal question={editing.question} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        title="Remove this question?"
        description="Past check-ins keep their answers."
        confirmLabel="Remove"
        loading={remove.isPending}
      />
    </Card>
  );
}

function QuestionModal({ question, onClose, onSaved }: { question: CheckinQuestion | null; onClose(): void; onSaved(): void }) {
  const toast = useToast();
  const [label, setLabel] = useState(question?.label ?? '');
  const [type, setType] = useState<IntakeQuestionType>(question?.type ?? 'CHECKBOX');
  const [options, setOptions] = useState(question?.options.join('\n') ?? '');
  const [required, setRequired] = useState(question?.required ?? false);

  const save = useMutation({
    mutationFn: () => {
      const body = { label, type, options: type === 'SELECT' ? lines(options) : [], required: type === 'CHECKBOX' ? false : required };
      return question ? api.patch(`/checkin-questions/${question.id}`, body) : api.post('/checkin-questions', body);
    },
    onSuccess: () => {
      onSaved();
      toast.success(question ? 'Question saved' : 'Question added');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={question ? 'Edit question' : 'Add a question'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="checkin-question" variant="primary" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="checkin-question" onSubmit={submit} className="space-y-4">
        <Field label="Question" required>
          <Input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={200} placeholder="e.g. Charger returned?" required autoFocus />
        </Field>
        <Field label="Answer">
          <Select value={type} onChange={(event) => setType(event.target.value as IntakeQuestionType)}>
            {(Object.keys(INTAKE_TYPE_LABEL) as IntakeQuestionType[]).map((value) => (
              <option key={value} value={value}>
                {INTAKE_TYPE_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>
        {type === 'SELECT' && (
          <Field label="Choices" hint="One per line">
            <Textarea rows={4} value={options} onChange={(event) => setOptions(event.target.value)} placeholder={'Good\nWorn\nMissing'} />
          </Field>
        )}
        {type !== 'CHECKBOX' && <Switch label="Required" description="A check-in can’t be saved without an answer" checked={required} onChange={setRequired} />}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Loaners and reminders
// ---------------------------------------------------------------------------

function LoanerSettings({ settings }: { settings: CheckoutSettings }) {
  const save = useSaveSettings('Loaner settings saved');
  const [days, setDays] = useState(String(settings.loanerDays));
  const [time, setTime] = useState(settings.sameDayReturnTime);

  return (
    <Card>
      <CardHeader
        title="Loaners"
        description="Devices in the loaner pool are lent while someone’s own device is repaired, or for the day when they forgot it."
        actions={
          <Link to="/loaners" className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
            Loaner dashboard <ExternalLink className="size-3.5" />
          </Link>
        }
      />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({ loanerDays: Number(days), sameDayReturnTime: time });
        }}
        className="max-w-2xl space-y-4 p-5"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Repair loaners are due back after" hint="Each loan’s date can be changed when it’s lent.">
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={60} value={days} onChange={(event) => setDays(event.target.value)} className="w-24" required />
              <span className="text-sm text-slate-600">days</span>
            </div>
          </Field>
          <Field label="Same-day loaners are due back at" hint="Lent after this time, it’s due at this time the next day.">
            <Input type="time" value={time} onChange={(event) => setTime(event.target.value)} className="w-32" required />
          </Field>
        </div>
        <p className="text-sm text-slate-600">
          Put a device in the loaner pool by editing it and checking <strong>In the loaner pool</strong>.
        </p>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Save
        </Button>
      </form>
    </Card>
  );
}

function PolicyRow({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-600">{children}</div>
    </div>
  );
}

function DaysInput({ value, onChange, min }: { value: number | null; onChange(value: number | null): void; min: number }) {
  return (
    <Input
      type="number"
      min={min}
      max={120}
      value={value ?? ''}
      placeholder="—"
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      className="w-20"
    />
  );
}

function PolicyEditor({ title, description, value, onChange }: { title: string; description: string; value: ReminderPolicy; onChange(value: ReminderPolicy): void }) {
  const [emails, setEmails] = useState(value.escalateEmails.join(', '));
  const set = <K extends keyof ReminderPolicy>(key: K, next: ReminderPolicy[K]) => onChange({ ...value, [key]: next });

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 p-4">
      <Switch label={title} description={description} checked={value.enabled} onChange={(enabled) => set('enabled', enabled)} />
      {value.enabled && (
        <div className="space-y-3 border-t border-slate-100 pt-4">
          <PolicyRow label="Before it’s due" hint="Blank for none">
            <DaysInput value={value.preDueDays} min={1} onChange={(days) => set('preDueDays', days)} /> days before
          </PolicyRow>
          <Switch label="On the day it’s due" description="Or an hour before, when it was lent that same day" checked={value.dueDay} onChange={(on) => set('dueDay', on)} />
          <PolicyRow label="Overdue" hint="0 sends it as soon as it’s late">
            <DaysInput value={value.overdueDays} min={0} onChange={(days) => set('overdueDays', days)} /> days after
          </PolicyRow>
          <PolicyRow label="Last reminder" hint="Also tells the technology team. Blank for none.">
            <DaysInput value={value.escalateDays} min={1} onChange={(days) => set('escalateDays', days)} /> days after
          </PolicyRow>
          <Switch
            label="Notify technicians and administrators"
            description="In the app, with the last reminder"
            checked={value.notifyStaff}
            onChange={(on) => set('notifyStaff', on)}
          />
          <Field label="Also email" hint="With the last reminder, like a dean or the front office. Separate addresses with commas.">
            <Input
              value={emails}
              onChange={(event) => {
                setEmails(event.target.value);
                set(
                  'escalateEmails',
                  event.target.value
                    .split(/[,;\s]+/)
                    .map((address) => address.trim())
                    .filter(Boolean),
                );
              }}
              placeholder="dean@school.org"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function ReminderSettings({ settings }: { settings: CheckoutSettings }) {
  const save = useSaveSettings('Reminder schedules saved');
  const [loaner, setLoaner] = useState(settings.loanerReminders);
  const [standard, setStandard] = useState(settings.standardReminders);

  return (
    <Card>
      <CardHeader
        title="Return reminders"
        description="Emails asking borrowers to bring devices back: before the due date, on it, once it’s overdue, and a last one that also tells the technology team."
      />
      <div className="space-y-5 p-5">
        {settings.mailbox.ready ? (
          <p className="text-sm text-slate-600">
            Sent from {settings.mailbox.address} during the help desk’s business hours, once for each step. Staff can also send one by hand from the checkout and
            loaner lists.
          </p>
        ) : (
          <Alert tone="amber" title="Help desk email isn’t set up">
            Reminders are emailed from the help desk mailbox.{' '}
            <Link to="/integrations/email" className="font-medium underline">
              Set up help desk email
            </Link>{' '}
            to send them.
          </Alert>
        )}
        <div className="grid gap-5 lg:grid-cols-2">
          <PolicyEditor title="Loaners" description="Usually due back within days, so reminders matter most." value={loaner} onChange={setLoaner} />
          <PolicyEditor title="Checkouts" description="Everything else with a due date, like devices out for the year." value={standard} onChange={setStandard} />
        </div>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate({ loanerReminders: loaner, standardReminders: standard })}>
          Save
        </Button>
      </div>
    </Card>
  );
}
